import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Code2, Copy, CheckCircle2, FileJson, Info } from "lucide-react";
import { useTranslation } from "../contexts/LocaleContext";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function JsonSpecificationModal({ isOpen, onClose }: Props) {
  const t = useTranslation();
  const [copied, setCopied] = React.useState(false);

  const handleCopyLlm = () => {
    const prompt = `Act as a data migration expert. Your task is to convert invoice data from various formats (text, CSV, images, or other JSON structures) into the specific JSON format required by the Invoitio invoice generator.

### TARGET JSON STRUCTURE:
{
  "invoices": [
    {
      "draft": {
        "invoiceNo": "string",
        "issueDate": "string (YYYY-MM-DD)",
        "dueDate": "string (YYYY-MM-DD)",
        "currency": "string (e.g., 'CZK')",
        "buyerName": "string",
        "buyerAddress": "string",
        "buyerId": "string (IČO)",
        "buyerTaxId": "string (optional, DIČ)",
        "items": [
          {
            "id": "string (unique id)",
            "description": "string",
            "quantity": number,
            "amount": number,
            "total": number
          }
        ],
        "total": number,
        "note": "string (optional)",
        "vatEnabled": boolean (optional),
        "vatRate": number (optional),
        "sellerInfo": {
          "name": "string",
          "address": "string",
          "identificationNumber": "string",
          "country": "string"
        }
      },
      "designSettings": {
        "template": "Стандартный",
        "fontFamily": "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, \"Helvetica Neue\", Arial",
        "accentColor": "#2563eb",
        "textColor": "#000000",
        "backgroundColor": "#ffffff"
      },
      "language": "cs"
    }
  ]
}

### INSTRUCTIONS:
1. Ensure all dates are in YYYY-MM-DD format. Use 'issueDate' for the date of the invoice.
2. All numeric values must be numbers. Use 'amount' for unit price.
3. 'buyerId' is the company registration number (IČO).
4. ALWAYS use the following fixed 'designSettings': {"template": "Стандартный", "fontFamily": "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, \"Helvetica Neue\", Arial", "accentColor": "#2563eb", "textColor": "#000000", "backgroundColor": "#ffffff"}.
5. Output ONLY the final valid JSON object. No explanations.

Please convert the following data:
[PASTE YOUR DATA HERE]`;

    navigator.clipboard.writeText(prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-4xl max-h-[90vh] bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 flex flex-col"
          >
            {/* Header */}
            <div className="p-6 md:p-8 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/50">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-2xl flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                  <FileJson size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                    {(t as any).transferInvoices.specificationTitle}
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                    Technical documentation for automatic transfer
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleCopyLlm}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                    copied
                      ? "bg-emerald-500 text-white"
                      : "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:border-indigo-500"
                  }`}
                >
                  {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
                  {(t as any).transferInvoices.copyLlmButton}
                </button>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-slate-400"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 md:p-10 space-y-10">
              {/* Introduction */}
              <section className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-800/30 text-blue-700 dark:text-blue-300">
                  <Info className="w-5 h-5 shrink-0 mt-0.5" />
                  <p className="text-sm font-medium leading-relaxed">
                    Our system accepts an object containing an <code>invoices</code> array. Each invoice must include <code>draft</code>, <code>designSettings</code>, and <code>language</code>.
                  </p>
                </div>
              </section>

              {/* Specification Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <h4 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <div className="w-1.5 h-6 bg-indigo-500 rounded-full" />
                    Draft Object (Data)
                  </h4>
                  <div className="space-y-4">
                    <SpecItem label="invoiceNo" type="string" description="Unique number of the invoice (e.g. 'INV-2024-001')" />
                    <SpecItem label="issueDate" type="string" description="Date of issue in YYYY-MM-DD format" />
                    <SpecItem label="dueDate" type="string" description="Due date in YYYY-MM-DD format" />
                    <SpecItem label="currency" type="string" description="Currency code (CZK, EUR, USD, etc.)" />
                    <SpecItem label="buyerName" type="string" description="Full name or company name of the client" />
                    <SpecItem label="buyerAddress" type="string" description="Full address of the client" />
                    <SpecItem label="buyerId" type="string" description="Company registration number (IČO)" />
                    <SpecItem label="buyerTaxId" type="string" description="VAT number (DIČ) - optional" />
                    <SpecItem label="note" type="string" description="Additional note on the invoice (optional)" />
                    <SpecItem label="vatEnabled" type="boolean" description="Enable VAT calculation (optional)" />
                    <SpecItem label="vatRate" type="number" description="VAT percentage, e.g. 21 (optional)" />
                    <SpecItem label="items[].id" type="string" description="Unique ID for the item line" />
                    <SpecItem label="items[].amount" type="number" description="Unit price (price per 1)" />
                  </div>

                  <h4 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2 pt-4">
                    <div className="w-1.5 h-6 bg-amber-500 rounded-full" />
                    Seller Information
                  </h4>
                  <div className="space-y-4">
                    <SpecItem label="sellerInfo.name" type="string" description="Your business name" />
                    <SpecItem label="sellerInfo.address" type="string" description="Your business address" />
                    <SpecItem label="sellerInfo.identificationNumber" type="string" description="Your IČO" />
                    <SpecItem label="sellerInfo.dic" type="string" description="Your DIČ (optional)" />
                    <SpecItem label="sellerInfo.country" type="string" description="Business country" />
                  </div>
                </div>

                <div className="space-y-6">
                  <h4 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <div className="w-1.5 h-6 bg-purple-500 rounded-full" />
                    Design & Locale
                  </h4>
                  <div className="space-y-4">
                    <SpecItem label="language" type="'cs' | 'en' | 'ru'" description="Language of the invoice document" />
                    <SpecItem label="designSettings.template" type="string" description="Default: 'Стандартный'" />
                    <SpecItem label="designSettings.fontFamily" type="string" description="System fonts preferred" />
                    <SpecItem label="designSettings.accentColor" type="hex-string" description="Primary color (e.g. '#2563eb')" />
                    <SpecItem label="designSettings.textColor" type="hex-string" description="Text color (default: '#000000')" />
                    <SpecItem label="designSettings.backgroundColor" type="hex-string" description="Background color (default: '#ffffff')" />
                  </div>

                  <h4 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2 pt-4">
                    <div className="w-1.5 h-6 bg-emerald-500 rounded-full" />
                    Items (Array)
                  </h4>
                  <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 text-xs font-mono space-y-2">
                    <p className="text-slate-400">// Example item structure</p>
                    <p>{"{"}</p>
                    <p className="pl-4">"id": "item-1",</p>
                    <p className="pl-4">"description": "Consulting services",</p>
                    <p className="pl-4">"quantity": 10,</p>
                    <p className="pl-4">"amount": 1200,</p>
                    <p className="pl-4">"total": 12000</p>
                    <p>{"}"}</p>
                  </div>
                </div>
              </div>

              {/* Full Example Code */}
              <section className="space-y-4">
                <h4 className="text-lg font-bold text-slate-900 dark:text-white">Full Structure Example</h4>
                <div className="relative group">
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(EXAMPLE_CODE, null, 2));
                      }}
                      className="p-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700"
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                  <pre className="bg-slate-900 text-slate-300 p-6 rounded-3xl text-sm font-mono overflow-x-auto shadow-inner leading-relaxed">
                    {JSON.stringify(EXAMPLE_CODE, null, 2)}
                  </pre>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function SpecItem({ label, type, description }: { label: string; type: string; description: string }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="text-indigo-600 dark:text-indigo-400 font-bold text-sm">{label}</span>
        <span className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded-md font-mono uppercase tracking-wider">
          {type}
        </span>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
        {description}
      </p>
    </div>
  );
}

const EXAMPLE_CODE = {
  "invoices": [
    {
      "draft": {
        "invoiceNo": "2024001",
        "issueDate": "2024-01-01",
        "dueDate": "2024-01-14",
        "buyerName": "Acme Corp",
        "buyerAddress": "Prague, Czech Republic",
        "buyerId": "12345678",
        "items": [
          {
            "id": "item-1",
            "description": "Software Development",
            "quantity": 1,
            "amount": 5000,
            "total": 5000
          }
        ],
        "total": 5000,
        "currency": "CZK",
        "sellerInfo": {
          "name": "My Business s.r.o.",
          "address": "Dlouhá 123, Prague",
          "identificationNumber": "87654321",
          "country": "Czech Republic"
        }
      },
      "designSettings": {
        "template": "Стандартный",
        "fontFamily": "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, \"Helvetica Neue\", Arial",
        "accentColor": "#2563eb",
        "textColor": "#000000",
        "backgroundColor": "#ffffff"
      },
      "language": "cs"
    }
  ]
};
